// src/components/ScrollFadeArea/ScrollFadeArea.tsx
import React, { forwardRef } from 'react';
import styled from 'styled-components';
import { MEDIA_BREAKPOINTS, SHORT_HEIGHT } from '@/constants/layout';

const ULTRA_NARROW = '380px';

const StyledScrollArea = styled.div<{
  $maxDesktop?: number;
  $maxMobile?: number;
  $maxUltra?: number;
  $alwaysShowIndicators?: boolean;
}>`
  position: relative;

  /* 🔥 scrollbar מימין */
  direction: ltr;

  overflow: auto;

  /* ⭐️ שומר מקום קבוע */
  scrollbar-gutter: stable;

  -webkit-overflow-scrolling: touch;

  /* 🔥 נכנס לתוך ה-padding של ה-Bubble */
  margin-inline-end: -12px;
  padding-inline-end: 12px;

  --fade-size-desktop: 28px;
  --fade-size-mobile: 18px;
  --fade-size-ultra: 14px;
  --fade-size: var(--fade-size-desktop);

  mask-image: linear-gradient(
    to bottom,
    rgba(0,0,0,1) 0%,
    rgba(0,0,0,1) calc(100% - var(--fade-size)),
    rgba(0,0,0,0) 100%
  );
  -webkit-mask-image: linear-gradient(
    to bottom,
    rgba(0,0,0,1) 0%,
    rgba(0,0,0,1) calc(100% - var(--fade-size)),
    rgba(0,0,0,0) 100%
  );

  padding-bottom: calc(var(--fade-size) + 10px);

  /* ===== Fade ===== */

  &::before,
  &::after {
    content: "";
    position: sticky;
    left: 0;
    right: 0;
    pointer-events: none;
    z-index: 1;
    transition: opacity .18s ease;
  }

  &::before {
    top: 0;
    height: var(--fade-size);
    background: linear-gradient(to top, rgba(0,0,0,0) 0%, rgba(0,0,0,0.1) 100%);
    opacity: 0;
  }

  &::after {
    bottom: 0;
    height: var(--fade-size);
    background: linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.12) 100%);
    opacity: 1;
  }

  &[data-at-top="true"]::before {
    opacity: ${({ $alwaysShowIndicators }) => ($alwaysShowIndicators ? 1 : 0)};
  }
  &[data-at-top="false"]::before { opacity: 1; }
  &[data-at-end="true"]::after {
    opacity: ${({ $alwaysShowIndicators }) => ($alwaysShowIndicators ? 1 : 0)};
  }
  &[data-at-end="false"]::after { opacity: 1; }

  /* ===== Scrollbar ===== */

  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.titleColor} transparent;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.titleColor};
    border-radius: 10px;
    border: 2px solid transparent;
    background-clip: content-box;
  }

  /* ===== Heights ===== */

  max-height: ${({ $maxDesktop }) => ($maxDesktop ?? 360)}px;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    max-height: ${({ $maxMobile }) => ($maxMobile ?? 150)}px;
    --fade-size: var(--fade-size-mobile);

    &::-webkit-scrollbar {
      width: 6px;
    }
  }

  @media (max-width: ${ULTRA_NARROW}) {
    max-height: ${({ $maxUltra }) => ($maxUltra ?? 105)}px;
    --fade-size: var(--fade-size-ultra);
  }

  @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) and (max-height: ${SHORT_HEIGHT}) {
    max-height: ${({ $maxDesktop }) =>
      Math.round(($maxDesktop ?? 360) * 0.75)}px;
    --fade-size: 22px;
  }

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) and (max-height: ${SHORT_HEIGHT}) {
    max-height: ${({ $maxMobile }) =>
      Math.round(($maxMobile ?? 150) * 0.75)}px;
    --fade-size: 16px;
  }

  @media (max-width: ${ULTRA_NARROW}) and (max-height: ${SHORT_HEIGHT}) {
    max-height: ${({ $maxUltra }) =>
      Math.round(($maxUltra ?? 105) * 0.7)}px;
    --fade-size: 12px;
  }

  /* Keep scroll mechanics independent, but let content follow the real page dir. */

  & > .scroll-content {
    overflow-wrap: anywhere;
    word-break: break-word;
    hyphens: auto;
  }

  html[dir='rtl'] & > .scroll-content {
    direction: rtl;
    text-align: right;
  }

  html[dir='ltr'] & > .scroll-content {
    direction: ltr;
    text-align: left;
  }
`;

const ScrollFadeArea = forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<{
    maxDesktop?: number;
    maxMobile?: number;
    maxUltra?: number;
    ariaLabel?: string;
    alwaysShowIndicators?: boolean;
  }>
>(({ children, maxDesktop, maxMobile, maxUltra, ariaLabel, alwaysShowIndicators }, ref) => (
  <StyledScrollArea
    ref={ref as any}
    $maxDesktop={maxDesktop}
    $maxMobile={maxMobile}
    $maxUltra={maxUltra}
    $alwaysShowIndicators={alwaysShowIndicators}
    aria-label={ariaLabel}
  >
    <div className="scroll-content">
      {children}
    </div>
  </StyledScrollArea>
));

ScrollFadeArea.displayName = 'ScrollFadeArea';

export default ScrollFadeArea;
